/**
 * SpaceStarSeed.ts
 * Pooled Star Seed pickup dropped by W2 enemies on death.
 *
 * Stationary in world space — the camera passes it by at
 * {@link ../../constants/spaceConstants.SPACE_AUTO_SCROLL_SPEED}.
 * Each pickup carries a stable string id (built from the killing
 * enemy's `spawnId`) so the same kill cannot bank multiple ids in
 * `persistSpaceLevelCompletion`'s mergeIds path.
 *
 * Mirror of {@link ./SpaceShard.SpaceShard} shape; the two could share
 * a base class but the symmetry is small enough that an inheritance
 * chain would cost more than it saves.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module SpaceStarSeed
 */

import { BaseEntity } from './BaseEntity';
import type { LevelRuntime } from '../engine/gameState';
import { spaceState } from '../engine/spaceState';
import {
  SPACE_STAR_SEED_WIDTH_PX,
  SPACE_STAR_SEED_HEIGHT_PX,
  SPACE_STAR_SEED_OFFSCREEN_MARGIN_PX,
} from '../../constants/spaceLootConstants';

/**
 * Pooled space-side Star Seed pickup. Acquired from the pool in
 * `spaceLootSystem.maybeSpawnSpaceLoot()`.
 */
export class SpaceStarSeed extends BaseEntity {
  private static nextSpawnId = 1;

  /** Re-issued on every spawn so the renderer gets a stable key per "use". */
  public spawnId = 0;

  /** Stable per-drop id. Empty string while pooled. */
  public seedId = '';

  /** True while this pickup is in the active list. */
  public active = false;

  /** True once the ship has collected this pickup. */
  public collected = false;

  constructor() {
    super(0, 0, SPACE_STAR_SEED_WIDTH_PX, SPACE_STAR_SEED_HEIGHT_PX);
    this.dead = true;
  }

  /**
   * Initialise the pickup at a world position with a stable id.
   *
   * @param seedId - Stable id (e.g. `'2-1-loot-12'`).
   * @param x      - Spawn X (world space, top-left of hitbox).
   * @param y      - Spawn Y (world space, top-left of hitbox).
   */
  public spawn(seedId: string, x: number, y: number): void {
    this.spawnId = SpaceStarSeed.nextSpawnId++;
    this.seedId = seedId;
    this.x = x;
    this.y = y;
    this.vx = 0;
    this.vy = 0;
    this.width = SPACE_STAR_SEED_WIDTH_PX;
    this.height = SPACE_STAR_SEED_HEIGHT_PX;
    this.dead = false;
    this.active = true;
    this.collected = false;
  }

  /** Pool-recycle hook. */
  public reset(): void {
    this.seedId = '';
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.width = SPACE_STAR_SEED_WIDTH_PX;
    this.height = SPACE_STAR_SEED_HEIGHT_PX;
    this.dead = true;
    this.active = false;
    this.collected = false;
  }

  /**
   * Advance one tick. Pickups don't move, so the work is just the
   * camera-left cull — once the pickup's right edge passes the
   * camera's left margin it is gone for the session.
   *
   * @param dt    - Fixed timestep in seconds (unused — pickups don't move).
   * @param level - Unused (space has no level geometry).
   */
  public update(dt: number, level: LevelRuntime): void {
    void dt;
    void level;
    if (!this.active) return;
    const leftCullX = spaceState.cameraX - SPACE_STAR_SEED_OFFSCREEN_MARGIN_PX;
    if (this.x + this.width < leftCullX) {
      this.dead = true;
      this.active = false;
    }
  }
}
